import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  ChevronDown,
  Download,
  Eye,
  Filter,
  PackageCheck,
  RefreshCw,
  Search,
  Truck,
} from "lucide-react";
import { GetOrders, GetVendorsOrders, MyAccounts } from "../../common/CommonApi";

const tabs = [
  { key: "new", label: "New", statuses: ["pending", "processing"] },
  { key: "ready", label: "Ready To Ship", statuses: ["confirmed", "packed"] },
  { key: "pickup", label: "Pickups & Manifests", statuses: ["packed"] },
  { key: "transit", label: "In Transit", statuses: ["shipped", "out_for_delivery"] },
  { key: "delivered", label: "Delivered", statuses: ["delivered"] },
  { key: "rto", label: "RTO", statuses: ["cancelled", "returned", "rto"] },
  { key: "all", label: "All", statuses: [] },
];

const statusLabels = {
  pending: "New",
  processing: "Processing",
  confirmed: "Ready To Ship",
  packed: "Pickup Pending",
  shipped: "In Transit",
  out_for_delivery: "In Transit",
  delivered: "Delivered",
  cancelled: "Cancelled",
  returned: "RTO",
  rto: "RTO",
};

const PAGE_SIZE = 30;

const dateLabel = (startDate, endDate) => {
  if (!startDate && !endDate) return "All dates";

  const format = (value) =>
    new Date(value).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

  if (startDate && endDate) return `${format(startDate)} - ${format(endDate)}`;
  if (startDate) return `From ${format(startDate)}`;
  return `Until ${format(endDate)}`;
};

const formatAddress = (order) => {
  const address = order.addressInfo;
  if (!address) return order.address || "No pickup address";
  return [
    address.houseNoTowerBlock,
    address.addressStreetArea,
    address.localityTown,
    address.cityDistict,
    address.state,
    address.pincode,
  ]
    .filter(Boolean)
    .join(", ");
};

const getOrderAmount = (order) =>
  order.finalAmount || order.totalAmount || order.price * order.quantity || 0;

const normalizeStatus = (status = "") => status.toLowerCase();

const getDisplayOrderId = (order) => order.sourceOrderId || order._id;

const getCancelNote = (order) =>
  [order.cancelReason, order.cancelRemarks].filter(Boolean).join(" - ");

const getPrimaryItem = (order) => order.items?.[0] || {};

const getVendorName = (order) => {
  const item = getPrimaryItem(order);
  const vendor = order.vendor || item.vendor || item.product?.vendor || order.product?.vendor;
  return vendor?.companyName || vendor?.fullname || vendor?.legalName || vendor?.email || "Vendor not assigned";
};

const getCustomer = (order) =>
  order.user?.phoneNumber || order.user?.fullname || order.user?.email || "Unknown user";

const getProductSummary = (order) => {
  const item = getPrimaryItem(order);
  const product = item.product || order.product || {};
  const variant = item.variant || order.variant || product.variant || {};
  return {
    name: product.name || item.productName || order.name || "Product not found",
    image:
      variant.images?.[0] ||
      variant.varientImages?.[0] ||
      product.images?.[0] ||
      "/assets/defaultimage.png",
    variant: [variant.size || item.variantSize || order.size, variant.color || item.variantColor || order.color]
      .filter(Boolean)
      .join(" / "),
    quantity: item.quantity || order.quantity || 1,
  };
};

const getOrdersFromResponse = (response) => {
  const payload = response?.data;

  if (Array.isArray(payload?.data?.orders)) return payload.data;
  if (Array.isArray(payload?.data)) return { orders: payload.data, pagination: null };
  if (Array.isArray(payload)) return { orders: payload, pagination: null };

  return { orders: [], pagination: null };
};

const Orders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [subTab, setSubTab] = useState("new");
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchOrders = async (page = 1) => {
    setLoading(true);
    try {
      const currentAccount = await MyAccounts();
      const response = currentAccount?.isVendor
        ? await GetVendorsOrders(currentAccount._id, page, PAGE_SIZE)
        : await GetOrders(page, PAGE_SIZE);
      const result = getOrdersFromResponse(response);
      setOrders(result.orders);
      setPagination(result.pagination || { page, pages: 0, total: result.orders.length });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const filteredOrders = useMemo(() => {
    const currentTab = tabs.find((tab) => tab.key === activeTab);
    const start = startDate ? new Date(`${startDate}T00:00:00`) : null;
    const end = endDate ? new Date(`${endDate}T23:59:59`) : null;
    const query = searchTerm.trim().toLowerCase();

    return orders.filter((order) => {
      const status = normalizeStatus(order.status);
      const orderDate = new Date(order.createdDate || order.updatedDate || Date.now());
      const product = getProductSummary(order);
      const searchable = [
        getDisplayOrderId(order),
        product.name,
        product.variant,
        getCustomer(order),
        getVendorName(order),
        formatAddress(order),
        order.paymentMethod,
        status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const statusMatches =
        activeTab === "all" || currentTab?.statuses.includes(status);
      const subTabMatches = subTab === "onHold" ? status === "cancelled" : status !== "cancelled";
      const dateMatches = (!start || orderDate >= start) && (!end || orderDate <= end);
      const searchMatches = !query || searchable.includes(query);

      return statusMatches && subTabMatches && dateMatches && searchMatches;
    });
  }, [orders, activeTab, subTab, startDate, endDate, searchTerm]);

  const stats = useMemo(
    () => ({
      total: filteredOrders.length,
      ready: filteredOrders.filter((order) =>
        ["confirmed", "packed"].includes(normalizeStatus(order.status))
      ).length,
      transit: filteredOrders.filter((order) =>
        ["shipped", "out_for_delivery"].includes(normalizeStatus(order.status))
      ).length,
      delivered: filteredOrders.filter((order) => normalizeStatus(order.status) === "delivered").length,
    }),
    [filteredOrders]
  );

  return (
    <Page>
      <TopTabs>
        {tabs.map((tab) => (
          <TabButton
            key={tab.key}
            active={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </TabButton>
        ))}
      </TopTabs>

      <Toolbar>
        <Segmented>
          <Segment active={subTab === "new"} onClick={() => setSubTab("new")}>
            New
          </Segment>
          <Segment active={subTab === "onHold"} onClick={() => setSubTab("onHold")}>
            On Hold
          </Segment>
        </Segmented>

        <ToolbarRow>
          <DateRange>
            <Calendar size={20} />
            <span>{dateLabel(startDate, endDate)}</span>
            <ChevronDown size={18} />
            <DateInputs>
              <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
              <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
            </DateInputs>
          </DateRange>

          <SearchBox>
            <Search size={18} />
            <input
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search order, user, vendor, product"
            />
          </SearchBox>

          <FilterButton type="button">
            <Filter size={19} />
            More Filters
          </FilterButton>

          <Spacer />

          <IconButton type="button" title="Refresh orders" onClick={() => fetchOrders(pagination.page)}>
            <RefreshCw size={19} />
          </IconButton>
          <IconButton type="button" title="Download orders">
            <Download size={19} />
          </IconButton>
          <BulkSelect>
            <span>Select Bulk Action</span>
            <ChevronDown size={18} />
          </BulkSelect>
        </ToolbarRow>
      </Toolbar>

      <StatsRow>
        <Stat><strong>{stats.total}</strong><span>Total</span></Stat>
        <Stat><strong>{stats.ready}</strong><span>Ready</span></Stat>
        <Stat><strong>{stats.transit}</strong><span>In Transit</span></Stat>
        <Stat><strong>{stats.delivered}</strong><span>Delivered</span></Stat>
      </StatsRow>

      <TableShell>
        <TableHeader>
          <div>Order Details</div>
          <div>Product Details</div>
          <div>User & Vendor</div>
          <div>Pickup Address</div>
          <div>Status</div>
          <div>Action</div>
        </TableHeader>

        {loading ? (
          <EmptyState>Loading orders...</EmptyState>
        ) : filteredOrders.length ? (
          filteredOrders.map((order) => {
            const product = getProductSummary(order);
            const status = normalizeStatus(order.status);
            const displayOrderId = getDisplayOrderId(order);

            return (
              <TableRow key={order._id}>
                <OrderMeta>
                  <strong>#{displayOrderId?.slice(-8)?.toUpperCase()}</strong>
                  <span>{new Date(order.createdDate).toLocaleString()}</span>
                  <span>{order.paymentMethod || "Payment not set"} · ₹{getOrderAmount(order)}</span>
                </OrderMeta>

                <ProductCell>
                  <ProductImage src={product.image} alt={product.name} />
                  <div>
                    <strong>{product.name}</strong>
                    <span>{product.variant || "No variant"} · Qty {product.quantity}</span>
                  </div>
                </ProductCell>

                <StackedText>
                  <strong>{getCustomer(order)}</strong>
                  <span>{getVendorName(order)}</span>
                </StackedText>

                <AddressText>{formatAddress(order)}</AddressText>

                <StatusCell>
                  <StatusPill status={status}>
                    {statusLabels[status] || order.status || "Unknown"}
                  </StatusPill>
                  {status === "cancelled" && getCancelNote(order) && (
                    <CancelNote>{getCancelNote(order)}</CancelNote>
                  )}
                </StatusCell>

                <Actions>
                  <ActionButton type="button" onClick={() => navigate(`/orders/view/?id=${order._id}`)}>
                    <Eye size={16} />
                    View
                  </ActionButton>
                  <ActionButton type="button" onClick={() => navigate(`/orders/status/?id=${order._id}`)}>
                    <Truck size={16} />
                    Track
                  </ActionButton>
                </Actions>
              </TableRow>
            );
          })
        ) : (
          <EmptyState>
            <PackageCheck size={28} />
            No orders found for this filter.
          </EmptyState>
        )}
      </TableShell>

      <PaginationBar>
        <span>Page {pagination.page || 1} of {pagination.pages || 1} · {pagination.total} orders</span>
        <div>
          <button type="button" disabled={loading || pagination.page <= 1} onClick={() => fetchOrders(pagination.page - 1)}>← Prev</button>
          <button type="button" disabled={loading || !pagination.pages || pagination.page >= pagination.pages} onClick={() => fetchOrders(pagination.page + 1)}>Next →</button>
        </div>
      </PaginationBar>
    </Page>
  );
};

export default Orders;

const Page = styled.div`
  min-height: calc(100vh - 120px);
  color: #202124;
`;

const TopTabs = styled.div`
  display: flex;
  gap: 34px;
  border-bottom: 1px solid #dedede;
  overflow-x: auto;
  margin: -10px -8px 24px;
  padding: 0 8px;
  scrollbar-width: thin;

  @media (max-width: 640px) {
    gap: 8px;
    margin-bottom: 18px;
  }
`;

const TabButton = styled.button`
  appearance: none;
  background: transparent;
  border: 0;
  border-bottom: 4px solid ${({ active }) => (active ? "#7c5ce6" : "transparent")};
  color: ${({ active }) => (active ? "#7c5ce6" : "#858585")};
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  padding: 0 16px 16px;
  white-space: nowrap;

  @media (max-width: 640px) {
    font-size: 14px;
    padding: 0 10px 12px;
  }
`;

const Toolbar = styled.div`
  display: grid;
  gap: 24px;
`;

const Segmented = styled.div`
  align-items: center;
  background: #ffffff;
  border: 1px solid #dedede;
  border-radius: 6px;
  display: inline-flex;
  overflow: hidden;
  width: max-content;

  @media (max-width: 480px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const Segment = styled.button`
  background: ${({ active }) => (active ? "#eee8ff" : "#ffffff")};
  border: 0;
  color: ${({ active }) => (active ? "#7c5ce6" : "#111827")};
  cursor: pointer;
  font-weight: 700;
  min-width: 84px;
  padding: 12px 18px;
`;

const ToolbarRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 14px;

  @media (max-width: 700px) {
    align-items: stretch;

    > * {
      max-width: 100%;
    }
  }
`;

const DateRange = styled.div`
  align-items: center;
  border: 1px solid #7c5ce6;
  border-radius: 6px;
  display: flex;
  gap: 12px;
  min-height: 46px;
  padding: 0 14px;
  position: relative;

  &:hover > div {
    display: flex;
  }

  @media (max-width: 700px) {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
  }
`;

const DateInputs = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.16);
  display: none;
  gap: 10px;
  left: 0;
  padding: 12px;
  position: absolute;
  top: calc(100% + 8px);
  z-index: 5;

  input {
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 8px;
  }

  @media (max-width: 700px) {
    position: static;
    display: grid;
    grid-template-columns: 1fr 1fr;
    flex-basis: 100%;
    width: 100%;
    padding: 10px 0 4px;
    border: 0;
    box-shadow: none;

    input {
      min-width: 0;
      width: 100%;
    }
  }

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

const SearchBox = styled.label`
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  display: flex;
  gap: 8px;
  min-height: 46px;
  padding: 0 12px;

  input {
    border: 0;
    min-width: 260px;
    outline: none;
  }

  @media (max-width: 700px) {
    width: 100%;

    input {
      min-width: 0;
      width: 100%;
    }
  }
`;

const FilterButton = styled.button`
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  gap: 10px;
  min-height: 46px;
  padding: 0 18px;

  @media (max-width: 480px) {
    flex: 1;
    justify-content: center;
  }
`;

const Spacer = styled.div`
  flex: 1;

  @media (max-width: 700px) {
    display: none;
  }
`;

const IconButton = styled.button`
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  height: 46px;
  justify-content: center;
  width: 54px;
`;

const BulkSelect = styled.button`
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #9ca3af;
  display: inline-flex;
  gap: 40px;
  min-height: 46px;
  padding: 0 16px;

  @media (max-width: 700px) {
    flex: 1;
    justify-content: space-between;
  }
`;

const StatsRow = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(4, minmax(120px, 1fr));
  margin: 20px 0;

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const Stat = styled.div`
  background: #ffffff;
  border: 1px solid #e8eef5;
  border-radius: 8px;
  padding: 14px 16px;

  strong {
    display: block;
    font-size: 22px;
  }

  span {
    color: #64748b;
    font-size: 13px;
  }
`;

const TableShell = styled.div`
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 12px 34px rgba(15, 23, 42, 0.08);
  overflow-x: auto;
`;

const tableColumns = "1.2fr 1.7fr 1.2fr 1.6fr 0.9fr 1.1fr";

const TableHeader = styled.div`
  background: #ffffff;
  border-bottom: 1px solid #eef2f7;
  display: grid;
  font-weight: 800;
  gap: 16px;
  grid-template-columns: ${tableColumns};
  min-width: 1080px;
  padding: 18px 22px;

  @media (max-width: 760px) {
    display: none;
  }
`;

const TableRow = styled.div`
  align-items: center;
  border-bottom: 1px solid #eef2f7;
  display: grid;
  gap: 16px;
  grid-template-columns: ${tableColumns};
  min-width: 1080px;
  padding: 18px 22px;

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 760px) {
    min-width: 0;
    grid-template-columns: 1fr;
    gap: 14px;
    margin: 10px;
    padding: 16px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.05);

    &:last-child {
      border-bottom: 1px solid #e2e8f0;
    }
  }
`;

const OrderMeta = styled.div`
  display: grid;
  gap: 5px;

  span {
    color: #64748b;
    font-size: 13px;
  }
`;

const ProductCell = styled.div`
  align-items: center;
  display: grid;
  gap: 12px;
  grid-template-columns: 54px 1fr;

  span {
    color: #64748b;
    display: block;
    font-size: 13px;
    margin-top: 4px;
  }
`;

const ProductImage = styled.img`
  aspect-ratio: 1;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  object-fit: cover;
  width: 54px;
`;

const StackedText = styled.div`
  display: grid;
  gap: 4px;

  span {
    color: #64748b;
    font-size: 13px;
  }
`;

const AddressText = styled.div`
  color: #475569;
  font-size: 13px;
  line-height: 1.4;
`;

const StatusCell = styled.div`
  display: grid;
  gap: 6px;
`;

const CancelNote = styled.span`
  color: #991b1b;
  font-size: 12px;
  line-height: 1.3;
`;

const StatusPill = styled.span`
  background: ${({ status }) =>
    status === "delivered"
      ? "#dcfce7"
      : ["cancelled", "returned", "rto"].includes(status)
      ? "#fee2e2"
      : ["shipped", "out_for_delivery"].includes(status)
      ? "#dbeafe"
      : "#eee8ff"};
  border-radius: 999px;
  color: ${({ status }) =>
    status === "delivered"
      ? "#166534"
      : ["cancelled", "returned", "rto"].includes(status)
      ? "#991b1b"
      : ["shipped", "out_for_delivery"].includes(status)
      ? "#1d4ed8"
      : "#6d4de0"};
  display: inline-flex;
  font-size: 12px;
  font-weight: 800;
  justify-content: center;
  padding: 7px 10px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  @media (max-width: 480px) {
    button {
      flex: 1;
      justify-content: center;
      min-height: 42px;
    }
  }
`;

const ActionButton = styled.button`
  align-items: center;
  background: #ffffff;
  border: 1px solid #dbe3ef;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  font-weight: 700;
  gap: 6px;
  padding: 8px 10px;

  &:hover {
    border-color: #7c5ce6;
    color: #6d4de0;
  }
`;

const EmptyState = styled.div`
  align-items: center;
  color: #64748b;
  display: flex;
  gap: 10px;
  justify-content: center;
  min-height: 260px;
`;

const PaginationBar = styled.div`
  align-items: center;
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  padding: 18px;

  button {
    background: transparent;
    border: 0;
    color: #4b5563;
    cursor: pointer;
    font-weight: 800;
    margin-left: 28px;
  }

  span { color: #64748b; font-size: 14px; margin-right: auto; }
  button:disabled { cursor: not-allowed; opacity: .45; }

  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column;
    gap: 14px;

    span {
      margin-right: 0;
      text-align: center;
    }

    div {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }

    button {
      min-height: 42px;
      margin-left: 0;
    }
  }
`;
